import React, { useState, useEffect, useRef } from 'react';
import './CourseManagement.css';
import CredentialDetailsModal from './CredentialDetailsModal';
import CourseDetailsModal from './CourseDetailsModal';
import { getRecommendedCourseDetails, getPotentialCourses, deletePotentialCourse } from '../services/api';
import { useNotification, NotificationContainer } from './Notification';

const CourseManagement = ({ onBack, programName, templateId, school: propSchool }) => {
  // Get school from URL parameters or props
  const getSchoolFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('school') || propSchool || 'MARYLAND';
  };

  const school = getSchoolFromUrl();
  
  // Use notification system
  const { notifications, addNotification, removeNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('current');
  const [expandedCourses, setExpandedCourses] = useState(new Set());
  const [showCredentialDetails, setShowCredentialDetails] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [recommendedCoursesData, setRecommendedCoursesData] = useState(null);
  const [potentialCoursesData, setPotentialCoursesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // PDF Preview Modal state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
  const [selectedPdfTitle, setSelectedPdfTitle] = useState('');
  const [pdfPreviewError, setPdfPreviewError] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState('');
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);
  const [selectedCourseCategory, setSelectedCourseCategory] = useState('general-education');
  const [selectedCourseForModal, setSelectedCourseForModal] = useState(null);
  const [showCourseDetailsModal, setShowCourseDetailsModal] = useState(false);
  const [addedFromRecommendations, setAddedFromRecommendations] = useState(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);
  const [selectedModuleForModal, setSelectedModuleForModal] = useState(null);
  const [showModuleDetailsModal, setShowModuleDetailsModal] = useState(false);
  const tabContainerRef = useRef(null);

  const toggleCourseExpansion = (courseIndex) => {
    setExpandedCourses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(courseIndex)) {
        newSet.delete(courseIndex);
      } else {
        newSet.add(courseIndex);
      }
      return newSet;
    });
  };

  // Update underline position when activeTab changes
  useEffect(() => {
    const updateUnderlinePosition = () => {
      if (tabContainerRef.current) {
        const activeTabElement = tabContainerRef.current.querySelector('.tab-item.active');
        
        if (activeTabElement) {
          const containerRect = tabContainerRef.current.getBoundingClientRect();
          const tabRect = activeTabElement.getBoundingClientRect();
          const left = tabRect.left - containerRect.left;
          const width = tabRect.width;
          
          tabContainerRef.current.style.setProperty('--underline-left', `${left}px`);
          tabContainerRef.current.style.setProperty('--underline-width', `${width}px`);
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(updateUnderlinePosition);
  }, [activeTab]);

  // Fetch recommended course details immediately when component loads
  useEffect(() => {
    const fetchRecommendedCourses = async () => {
      if (!templateId || !programName) {
        return;
      }
      
      // Only fetch if we haven't already loaded the data
      if (recommendedCoursesData) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await getRecommendedCourseDetails(templateId, programName);
        
        // Process the data to add program_job_id to each course from nested jobs array
        console.log('Raw API response:', data);
        
        if (data && data.courses) {
          const processedData = {
            ...data,
            courses: data.courses.map((course, index) => {
              console.log(`Processing course ${index}:`, course);
              
              // Use the course's id as program_job_id for the customize-syllabus endpoint
              const programJobId = course.id;
              console.log(`Using course id: ${programJobId} as program_job_id for course "${course.course_title}"`);
              
              return {
                ...course,
                program_job_id: programJobId,
                // Keep the existing syllabus_s3_url from the course
                syllabus_s3_url: course.syllabus_s3_url
              };
            })
          };
          console.log('Processed courses with program_job_id:', processedData);
          setRecommendedCoursesData(processedData);
        } else {
          console.log('Missing courses in API response');
          setRecommendedCoursesData(data);
        }
      } catch (err) {
        console.error('Error fetching recommended course details:', err);
        setError('Failed to load recommended courses');
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendedCourses();
  }, [templateId, programName]);

  // Fetch potential courses when switching to current courses tab
  useEffect(() => {
    const fetchPotentialCourses = async () => {
      if (!programName || activeTab !== 'current') {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const data = await getPotentialCourses(school, programName);
        setPotentialCoursesData(data);
      } catch (err) {
        console.error('Error fetching potential courses:', err);
        setError('Failed to load potential courses');
      } finally {
        setLoading(false);
      }
    };

    fetchPotentialCourses();
  }, [activeTab, programName, school]);

  // Handle credential details modal
  const handleViewCredentialDetails = (credential) => {
    setSelectedCredential(credential);
    setShowCredentialDetails(true);
  };

  const handleCloseCredentialDetails = () => {
    setShowCredentialDetails(false);
    setSelectedCredential(null);
  };

  // Fetch PDF and convert to blob URL
  const fetchPdfAsBlob = async (pdfUrl) => {
    try {
      setIsLoadingPdf(true);
      
      const response = await fetch(pdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      console.log('PDF converted to blob URL:', blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching PDF as blob:', error);
      throw error;
    } finally {
      setIsLoadingPdf(false);
    }
  };

  // Handle PDF preview
  const handleViewSyllabus = async (syllabusUrl, courseTitle, event) => {
    console.log('handleViewSyllabus called with:', { syllabusUrl, courseTitle });
    
    // Prevent any default behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (syllabusUrl && syllabusUrl !== 'xxx') {
      console.log('Opening PDF modal for:', syllabusUrl);
      setSelectedPdfUrl(syllabusUrl);
      setSelectedPdfTitle(courseTitle);
      setShowPdfModal(true);
      setPdfPreviewError(false);
      
      // Fetch PDF as blob URL for preview
      try {
        const blobUrl = await fetchPdfAsBlob(syllabusUrl);
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        console.error('Failed to load PDF preview:', error);
        setPdfPreviewError(true);
      }
    } else {
      console.log('Syllabus not available');
      // You can add a notification system here if needed
      addNotification('Syllabus not available for this course', 'warning');
    }
  };

  // Close PDF modal
  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setSelectedPdfUrl('');
    setSelectedPdfTitle('');
    setPdfPreviewError(false);
    
    // Clean up blob URL to prevent memory leaks
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl('');
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!selectedPdfUrl) {
      addNotification('No PDF file available for download', 'warning');
      return;
    }

    try {
      let downloadUrl;
      
      // Fetch the PDF from URL to create a blob
      console.log('Fetching PDF from URL for download:', selectedPdfUrl);
      const response = await fetch(selectedPdfUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      const blob = await response.blob();
      downloadUrl = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedPdfTitle}.pdf`;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
    } catch (error) {
      console.error('Download failed:', error);
      addNotification('Download failed. Please try again.', 'error');
    }
  };

  // Handle Add Course modal
  const handleAddCourse = (course) => {
    setSelectedCourseForModal(course);
    setShowAddCourseModal(true);
  };

  const handleCloseAddCourseModal = () => {
    setShowAddCourseModal(false);
    setSelectedCourseCategory('general-education');
    setSelectedCourseForModal(null);
  };

  // Handle Add Module modal
  const handleAddModule = (course) => {
    setSelectedModuleForModal(course);
    setShowAddModuleModal(true);
  };

  const handleCloseAddModuleModal = () => {
    setShowAddModuleModal(false);
    setSelectedModuleForModal(null);
  };

  const handleNextToModuleDetails = () => {
    if (!selectedModuleForModal) {
      addNotification('No module selected', 'warning');
      return;
    }

    // Close the current modal and open module details modal
    setShowAddModuleModal(false);
    setShowModuleDetailsModal(true);
  };

  const handleCloseModuleDetailsModal = () => {
    setShowModuleDetailsModal(false);
    // Reopen the add module modal
    setShowAddModuleModal(true);
  };

  const handleModuleDetailsSuccess = async () => {
    console.log('=== handleModuleDetailsSuccess CALLED ===');
    
    // Mark the module as added from recommendations
    if (selectedModuleForModal && selectedModuleForModal.course_title) {
      setAddedFromRecommendations(prev => new Set([...prev, selectedModuleForModal.course_title]));
      console.log('Marked module as added from recommendations:', selectedModuleForModal.course_title);
    }
    
    // Show success notification
    addNotification('Module added successfully!', 'success');
    
    // Reset all modal states
    setShowModuleDetailsModal(false);
    setShowAddModuleModal(false);
    setSelectedModuleForModal(null);

    // Refresh recommended courses data after successful module addition
    console.log('Refreshing recommended courses data after module addition...');
    
    if (templateId && programName) {
      setLoading(true);
      setError(null);
      
      try {
        console.log('=== CALLING getRecommendedCourseDetails for refresh ===');
        const data = await getRecommendedCourseDetails(templateId, programName);
        
        if (data && data.courses) {
          const processedData = {
            ...data,
            courses: data.courses.map((course, index) => {
              const programJobId = course.id;
              return {
                ...course,
                program_job_id: programJobId,
                syllabus_s3_url: course.syllabus_s3_url
              };
            })
          };
          setRecommendedCoursesData(processedData);
          console.log('=== MODULE REFRESH COMPLETED - NEW DATA SET ===');
        } else {
          setRecommendedCoursesData(data);
          console.log('=== MODULE REFRESH COMPLETED - ORIGINAL DATA SET ===');
        }
      } catch (err) {
        console.error('Error refreshing recommended course details:', err);
        setError('Failed to refresh recommended courses');
      } finally {
        setLoading(false);
        console.log('=== MODULE REFRESH PROCESS FINISHED ===');
      }
    }
  };

  const handleNextToCourseDetails = () => {
    if (!selectedCourseCategory) {
      addNotification('Please select a course category', 'warning');
      return;
    }

    if (!selectedCourseForModal) {
      addNotification('No course selected', 'warning');
      return;
    }

    // Close the current modal and open course details modal
    setShowAddCourseModal(false);
    setShowCourseDetailsModal(true);
  };

  const handleCategoryChange = (category) => {
    setSelectedCourseCategory(category);
  };

  const handleCloseCourseDetailsModal = () => {
    setShowCourseDetailsModal(false);
    // Reopen the add course modal
    setShowAddCourseModal(true);
  };

  // Refresh potential courses data
  const refreshPotentialCourses = async () => {
    if (!templateId || !programName) {
      console.log('Missing templateId or programName for refresh');
      return;
    }
    
    try {
      console.log('Refreshing potential courses data...');
      const data = await getPotentialCourses(school, programName);
      setPotentialCoursesData(data);
      console.log('Potential courses data refreshed:', data);
    } catch (error) {
      console.error('Error refreshing potential courses:', error);
    }
  };

  // Handle course deletion confirmation
  const handleDeleteCourse = (courseTitle) => {
    setCourseToDelete(courseTitle);
    setShowDeleteConfirmation(true);
  };

  // Confirm course deletion
  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;
    
    try {
      console.log('Deleting course:', courseToDelete);
      
      const response = await deletePotentialCourse(courseToDelete);
      console.log('Course deleted successfully:', response);
      
      // Remove from addedFromRecommendations state
      setAddedFromRecommendations(prev => {
        const newSet = new Set(prev);
        newSet.delete(courseToDelete);
        return newSet;
      });
      
      // Refresh the potential courses data
      await refreshPotentialCourses();
      
      addNotification('Course deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting course:', error);
      addNotification('Failed to delete course', 'error');
    } finally {
      setShowDeleteConfirmation(false);
      setCourseToDelete(null);
    }
  };

  // Cancel course deletion
  const cancelDeleteCourse = () => {
    setShowDeleteConfirmation(false);
    setCourseToDelete(null);
  };

  const handleCourseDetailsSuccess = async () => {
    console.log('=== handleCourseDetailsSuccess CALLED ===');
    
    // Mark the course as added from recommendations
    if (selectedCourseForModal && selectedCourseForModal.course_title) {
      setAddedFromRecommendations(prev => new Set([...prev, selectedCourseForModal.course_title]));
      console.log('Marked course as added from recommendations:', selectedCourseForModal.course_title);
    }
    
    // Show success notification
    addNotification('Course added successfully!', 'success');
    
    // Reset all modal states
    setShowCourseDetailsModal(false);
    setShowAddCourseModal(false);
    setSelectedCourseCategory('general-education');
    setSelectedCourseForModal(null);

    // Refresh recommended courses data after successful course addition
    console.log('Refreshing recommended courses data after course addition...');
    
    console.log('templateId from props:', templateId);
    console.log('programName from state:', programName);
    
    if (templateId && programName) {
      setLoading(true);
      setError(null);
      
      try {
        console.log('=== CALLING getRecommendedCourseDetails for refresh ===');
        console.log('templateId:', templateId);
        console.log('programName:', programName);
        
        const data = await getRecommendedCourseDetails(templateId, programName);
        
        // Process the data to add program_job_id to each course from nested jobs array
        console.log('Refreshed API response:', data);
        
        if (data && data.courses) {
          const processedData = {
            ...data,
            courses: data.courses.map((course, index) => {
              console.log(`Processing refreshed course ${index}:`, course);
              
              // Use the course's id as program_job_id for the customize-syllabus endpoint
              const programJobId = course.id;
              console.log(`Using course id: ${programJobId} as program_job_id for course "${course.course_title}"`);
              
              return {
                ...course,
                program_job_id: programJobId,
                // Keep the existing syllabus_s3_url from the course
                syllabus_s3_url: course.syllabus_s3_url
              };
            })
          };
          console.log('Processed refreshed courses with program_job_id:', processedData);
          setRecommendedCoursesData(processedData);
          console.log('=== REFRESH COMPLETED - NEW DATA SET ===');
        } else {
          console.log('Missing courses in refreshed API response');
          setRecommendedCoursesData(data);
          console.log('=== REFRESH COMPLETED - ORIGINAL DATA SET ===');
        }
      } catch (err) {
        console.error('Error refreshing recommended course details:', err);
        setError('Failed to refresh recommended courses');
      } finally {
        setLoading(false);
        console.log('=== REFRESH PROCESS FINISHED ===');
      }
    } else {
      console.log('=== REFRESH SKIPPED ===');
      console.log('templateId missing:', !templateId);
      console.log('programName missing:', !programName);
    }
  };

  // Process potential courses data from API
  const processPotentialCourses = () => {
    if (!potentialCoursesData) {
      return {
        generalEducation: [],
        electives: [],
        majorRequirement: [],
        modules: []
      };
    }

    const { courses_from_course_table, courses_from_selected_course_table } = potentialCoursesData;
    
    // Combine courses from both tables
    const allCourses = [
      ...courses_from_course_table.map(course => ({
        code: course.course_code, // Use course_code field
        title: course.course_name, // Use course_name field
        credits: 3, // Default credits since course_type is now the category
        courseType: course.course_type, // Store the course type for categorization
        source: 'course_table'
      })),
      ...courses_from_selected_course_table.map(course => ({
        code: course.course_code, // Use course_code field
        title: course.course_name, // Use course_name field
        credits: 3, // Default credits since course_type is now the category
        courseType: course.course_type, // Store the course type for categorization
        source: 'selected_course_table'
      }))
    ];

    // Categorize courses based on course_type
    const generalEducation = allCourses.filter(course => 
      course.courseType === 'General Education'
    );
    const electives = allCourses.filter(course => 
      course.courseType === 'Electives'
    );
    const majorRequirement = allCourses.filter(course => 
      course.courseType === 'Major Requirement'
    );
    const modules = allCourses.filter(course => 
      course.courseType === 'Module'
    );
    
    return {
      generalEducation,
      electives,
      majorRequirement,
      modules
    };
  };

  const courseData = processPotentialCourses();

  // Calculate actual course counts
  const getCurrentCoursesCount = () => {
    const { generalEducation, electives, majorRequirement, modules } = courseData;
    return generalEducation.length + electives.length + majorRequirement.length + modules.length;
  };

  const getRecommendedCoursesCount = () => {
    if (!recommendedCoursesData || !recommendedCoursesData.courses) {
      return 0;
    }
    return recommendedCoursesData.courses.length;
  };

  const renderCourseSection = (title, courses) => {
    // Calculate dynamic height based on actual CSS:
    // min-height: 60px + padding: 40px + margin-bottom: 17px = 117px per item
    // But last item has no margin-bottom, so subtract 17px
    const courseItemHeight = 60 // min-height + padding
    const courseMargin = 17;
    const totalHeight = courses.length > 0 ? 
      (courseItemHeight + courseMargin) * courses.length - courseMargin : 
      courseItemHeight;
    
    return (
      <div className="course-section">
        <div 
          className="course-category-label" 
          style={{ height: `${totalHeight}px` }}
        >
          <span>{title}</span>
          {title === 'Modules' && (
            <div className="module-info-tooltip">
              <img 
                src="/info-icon.svg" 
                alt="Info" 
                className="module-info-icon"
              />
            </div>
          )}
        </div>
        <div className="course-list">
          {courses.length > 0 ? (
            courses.map((course, index) => {
              const isFromRecommendations = addedFromRecommendations.has(course.title);
              const isFromSelectedCourseTable = course.source === 'selected_course_table';
              const showDeleteButton = isFromRecommendations || isFromSelectedCourseTable;
              
              return (
                <div key={index} className="course-item">
                  {isFromRecommendations && (
                    <span className="recommended-tag">New</span>
                  )}
                  <div className="course-code">
                    {course.courseType === 'Module' ? course.title : course.code}
                  </div>
                  <div className="course-details">
                    {course.courseType === 'Module' ? '' : `${course.title} | ${course.credits} Credits`}
                    {showDeleteButton && (
                      <div className="recommended-tag-container">
                        <img 
                          src="/delete.svg" 
                          alt="Delete" 
                          className="delete-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCourse(course.title);
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="course-item placeholder-item">
              <div className="course-details placeholder-text">No course under this category</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const RecommendedCoursesContent = () => {
    if (loading) {
      return (
    <div className="recommended-courses-content">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading recommended courses...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="recommended-courses-content">
          <div className="error-state">
            <p className="error-message">{error}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
            </div>
      );
    }

    if (!recommendedCoursesData || !recommendedCoursesData.courses) {
      return (
        <div className="recommended-courses-content">
          <div className="no-courses-state">
            <p>No recommended courses available.</p>
          </div>
        </div>
      );
    }

    return (
      <div className="recommended-courses-content">
        {/* Add All Button */}
        <button className="add-all-recommended-btn">+ Add All Recommended Courses</button>
        
        {/* Recommended Courses List */}
        <div className="recommended-courses-list">
          {recommendedCoursesData.courses.map((course, index) => {
          const isExpanded = expandedCourses.has(index);
          return (
            <div key={index} className="recommended-course-item">
              <div className="course-header" onClick={() => toggleCourseExpansion(index)}>
                <div className="course-management-info">
                  <div className={`arrow-icon ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </div>
                  <div className="course-management-title">{course.course_title}</div>
                </div>
                <div className="course-actions">
                  <button 
                    className="view-syllabus-btn" 
                    onClick={(e) => handleViewSyllabus(course.syllabus_s3_url || 'xxx', course.course_title, e)}
                  >
                    View Syllabus
                  </button>
                  <button className="course-add-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleAddCourse(course);
                  }}>
                    <span className="add-icon">+</span>
                    Add Course
                  </button>
                  <button className="course-add-module-btn" onClick={(e) => {
                    e.stopPropagation();
                    handleAddModule(course);
                  }}>
                    <span className="add-icon">+</span>
                    Add as Module
                  </button>
            </div>
          </div>
              
              {isExpanded && (
                <div className="course-expanded-content">
                  {/* Job Readiness Impact Section */}
                  <div className="impact-section">
                    <div className="impact-header">
                      <img src="/briefcase.svg" alt="Briefcase" className="category-icon" />
                      <h3>Job Readiness Impact</h3>
                    </div>
                    <div className="course-job-cards">
                      {course.jobs && course.jobs.map((job, jobIndex) => (
                        <div key={jobIndex} className="course-job-card">
                          <h4>{job.job_title}</h4>
            <div className="impact-row">
                            <div className="impact-score">
                              {job.overall_likelihood_percentage ? `+${job.overall_likelihood_percentage}%` : '+8%'}
                            </div>
              <div className="impact-label">Job Readiness</div>
            </div>
          </div>
                      ))}
        </div>
      </div>

                  {/* Learning Goals Impact Section */}
                  <div className="impact-section">
                    <div className="impact-header">
                      <img src="/target-icon.svg" alt="Target" className="category-icon" />
                      <h3>Learning Goals Impact</h3>
                    </div>
        <div className="learning-goals">
                      {recommendedCoursesData.learning_goals && Object.entries(recommendedCoursesData.learning_goals).map(([lgId, description], lgIndex) => {
                        const impact = course.learning_goals_impact && course.learning_goals_impact[lgId] ? course.learning_goals_impact[lgId] : 0;
                        return (
                          <div key={lgIndex} className="lg-item">
                            <div className="lg-badge">{lgId}</div>
                            <div className="lg-content">{description}</div>
                            <div className={`lg-impact ${impact >= 0 ? 'positive' : 'negative'}`}>
                              {impact >= 0 ? `+${impact}%` : `${impact}%`}
          </div>
          </div>
                        );
                      })}
        </div>
      </div>

                  {/* Credentials Section */}
                  <div className="credentials-section">
                    <div className="impact-header">
                      <img src="/credentials.svg" alt="Credentials" className="category-icon" />
                      <h3>Credential(s) to be earned</h3>
                    </div>
                    <div className="credentials-grid">
                      {course.micro_credentials && course.micro_credentials.map((credential, credIndex) => {
                        // Alternate badge colors: blue, green, pink
                        const badgeColors = ["/Badge-purple.svg", "/Badge-green.svg", "/Badge-pink.svg"];
                        const badgeImage = badgeColors[credIndex % badgeColors.length];
                        
                        return (
                        <div 
                          key={credIndex}
                          className="credential-card"
                          onClick={() => handleViewCredentialDetails({
                            id: credIndex + 1,
                            name: credential,
                            courseCount: 1,
                            courseCodes: [course.course_title],
                            badgeImage: badgeImage
                          })}
                        >
                          <div className="card-content">
                            <div className="badge-container">
                              <img 
                                src={badgeImage} 
                                alt={`${credential} Badge`}
                                className="badge-image"
                              />
          </div>
                            
                            <div className="bottom-section">
          <div className="credential-info">
                                <h3 className="credential-name">{credential}</h3>
                                <p className="credential-courses">1 Course Related</p>
                              </div>
                              
                              <button 
                                className="view-details-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewCredentialDetails({
                                    id: credIndex + 1,
                                    name: credential,
                                    courseCount: 1,
                                    courseCodes: [course.course_title],
                                    badgeImage: "/Badge-purple.svg"
                                  });
                                }}
                              >
                                <span>View Details</span>
                                <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3.08997 5.79004L8.29997 11L3.08997 16.21" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          </div>
          </div>
                        );
                      })}
        </div>
      </div>
            </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  };

  return (
    <div className="course-management-container">
      {/* Header */}
      <button className="back-button" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
      <div className="course-management-header">
        <h1>Course Management and Recommendations</h1>
        
        {/* School Year and Program Info */}
        <div className="header-info">
        <div className="info-row">
          <div className="info-item">
            <div className="info-text">
            <img src="/calendar.svg" alt="Calendar" className="info-icon" />
            <span className="info-label">School Year</span>
            <span className="info-value">2024–2025</span>
            </div>
          </div>
            <div className="info-item">
            <div className="info-text">
              <img src="/graduation-cap.svg" alt="Graduation Cap" className="info-icon" />
              <span className="info-label">Program</span>
              <span className="info-value program-tag">{programName || 'Program Assessment'}</span>
            </div>
            </div>
        </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="course-management-content">
        {/* Tab Header */}
        <div className="tab-header">
          <div className="tab-container" ref={tabContainerRef}>
            <div 
              className={`tab-item ${activeTab === 'current' ? 'active' : ''}`}
              onClick={() => setActiveTab('current')}
            >
              Current Courses ({getCurrentCoursesCount()})
            </div>
            <div 
              className={`tab-item ${activeTab === 'recommended' ? 'active' : ''}`}
              onClick={() => setActiveTab('recommended')}
            >
              Recommended Courses ({getRecommendedCoursesCount()})
            </div>
            <div className="tab-underline"></div>
          </div>
        </div>

        {/* Course Content */}
        <div className="course-content">
          {activeTab === 'current' ? (
            <div className="current-courses-content">
              <h2 className="course-sequence-title">Course Sequence</h2>
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading potential courses...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p className="error-message">{error}</p>
                  <button 
                    className="retry-button"
                    onClick={() => window.location.reload()}
                  >
                    Retry
                  </button>
                </div>
              ) : (
              <div className="course-sections">
                {renderCourseSection("General Education", courseData.generalEducation)}
                {renderCourseSection("Electives", courseData.electives)}
                {renderCourseSection("Major Requirement", courseData.majorRequirement)}
                {renderCourseSection("Modules", courseData.modules)}
              </div>
              )}
            </div>
          ) : (
            <div className="recommended-courses-content">
            <RecommendedCoursesContent />
            </div>
          )}
        </div>
      </div>
      
      {/* Credential Details Modal */}
      <CredentialDetailsModal
        isOpen={showCredentialDetails}
        onClose={handleCloseCredentialDetails}
        credential={selectedCredential}
        programName={programName}
        school="MARYLAND"
      />
      
      {/* PDF Preview Modal */}
      {showPdfModal && (
        <div className="pdf-modal-overlay" onClick={handleClosePdfModal}>
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-modal-header">
              <div className="pdf-title">{selectedPdfTitle}.pdf</div>
              <button className="pdf-download-btn" onClick={handleDownloadPdf}>
                Download
              </button>
              <button className="pdf-close-btn" onClick={handleClosePdfModal}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M9 3L3 9M3 3L9 9" stroke="#8E8E8E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="pdf-modal-content">
              {isLoadingPdf ? (
                <div className="pdf-loading">
                  <div className="loading-icon">⏳</div>
                  <h4>Loading PDF...</h4>
                  <p>Preparing PDF for preview...</p>
                </div>
              ) : !pdfPreviewError && pdfBlobUrl ? (
                <iframe
                  key={`pdf-preview-${selectedPdfTitle}`}
                  src={pdfBlobUrl}
                  title={selectedPdfTitle}
                  className="pdf-iframe"
                  frameBorder="0"
                  onError={() => {
                    console.log('PDF preview failed to load');
                    setPdfPreviewError(true);
                  }}
                  onLoad={() => {
                    console.log('PDF preview loaded successfully');
                  }}
                />
              ) : (
                <div className="preview-error">
                  <div className="error-icon">📄</div>
                  <h4>Preview not available</h4>
                  <p>Unable to load PDF preview.</p>
                  <button 
                    className="pdf-open-btn" 
                    onClick={() => window.open(selectedPdfUrl, '_blank')}
                  >
                    Open in New Tab
                  </button>
                </div>
              )}
              <div className="pdf-fallback">
                <p>Can't see the preview? 
                  <button 
                    className="preview-link-btn" 
                    onClick={() => window.open(selectedPdfUrl, '_blank')}
                  >
                    Click here to view the PDF
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Course Modal */}
      {showAddCourseModal && (
        <div className="add-course-modal-overlay" onClick={handleCloseAddCourseModal}>
          <div className="add-course-modal" onClick={(e) => e.stopPropagation()}>
            <button className="add-course-close-btn" onClick={handleCloseAddCourseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <div className="add-course-content">
              <div className="add-course-header">
                <h2 className="add-course-title">Add Course to Program</h2>
                <p className="add-course-subtitle">You are about to add the following course:</p>
              </div>

              <div className="course-preview-section">
                {selectedCourseForModal && (
                  <div className="course-preview-card">
                    <div className="course-preview-content">
                      <div className="course-preview-title-container">
                        <img src="/teacher.svg" alt="Teacher" className="teacher-icon" />
                        <div className="course-preview-title">{selectedCourseForModal.course_title}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="category-selection-section">
                <p className="category-label">Please select the course category:</p>
                
                <div className="checkbox-fields">
                  <div className="checkbox-field">
                    <div className="checkbox-row">
                      <input
                        type="radio"
                        id="general-education"
                        name="course-category"
                        checked={selectedCourseCategory === 'general-education'}
                        onChange={() => handleCategoryChange('general-education')}
                        className={`category-checkbox${selectedCourseCategory === 'general-education' ? ' category-checkbox--checked' : ''}`}
                        style={selectedCourseCategory === 'general-education' ? { accentColor: '#000000' } : {}}
                      />
                      <label htmlFor="general-education" className="category-checkbox-label">General Education</label>
                    </div>
                    <div className="checkbox-description">
                      <div className="checkbox-spacer"></div>
                      <span>Foundation Courses Required for All Students</span>
                    </div>
                  </div>

                  <div className="checkbox-field">
                    <div className="checkbox-row">
                      <input
                        type="radio"
                        id="electives"
                        name="course-category"
                        checked={selectedCourseCategory === 'electives'}
                        onChange={() => handleCategoryChange('electives')}
                        className={`category-checkbox${selectedCourseCategory === 'electives' ? ' category-checkbox--checked' : ''}`}
                        style={selectedCourseCategory === 'electives' ? { accentColor: '#000000' } : {}}
                      />
                      <label htmlFor="electives" className="category-checkbox-label">Electives</label>
                    </div>
                    <div className="checkbox-description">
                      <div className="checkbox-spacer"></div>
                      <span>Optional Courses to Enhance Learning</span>
                    </div>
                  </div>

                  <div className="checkbox-field">
                    <div className="checkbox-row">
                      <input
                        type="radio"
                        id="major-requirements"
                        name="course-category"
                        checked={selectedCourseCategory === 'major-requirements'}
                        onChange={() => handleCategoryChange('major-requirements')}
                        className={`category-checkbox${selectedCourseCategory === 'major-requirements' ? ' category-checkbox--checked' : ''}`}
                        style={selectedCourseCategory === 'major-requirements' ? { accentColor: '#000000' } : {}}
                      />
                      <label htmlFor="major-requirements" className="category-checkbox-label">Major Requirements</label>
                    </div>
                    <div className="checkbox-description">
                      <div className="checkbox-spacer"></div>
                      <span>Core Courses Required for the Major</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="add-course-actions">
                <button className="add-course-cancel-btn" onClick={handleCloseAddCourseModal}>
                  Cancel
                </button>
                <button className="add-course-confirm-btn" onClick={handleNextToCourseDetails}>
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Course Details Modal */}
      <CourseDetailsModal
        isOpen={showCourseDetailsModal}
        onClose={handleCloseCourseDetailsModal}
        selectedCourse={selectedCourseForModal}
        selectedCategory={selectedCourseCategory}
        programName={programName}
        recommendedCoursesData={recommendedCoursesData}
        onSuccess={handleCourseDetailsSuccess}
      />

      {/* Module Details Modal */}
      <CourseDetailsModal
        isOpen={showModuleDetailsModal}
        onClose={handleCloseModuleDetailsModal}
        selectedCourse={selectedModuleForModal}
        selectedCategory="module"
        programName={programName}
        recommendedCoursesData={recommendedCoursesData}
        onSuccess={handleModuleDetailsSuccess}
        type="module"
      />

      {/* Add Module Modal */}
      {showAddModuleModal && (
        <div className="add-module-modal-overlay" onClick={handleCloseAddModuleModal}>
          <div className="add-module-modal" onClick={(e) => e.stopPropagation()}>
            <button className="add-module-close-btn" onClick={handleCloseAddModuleModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            <div className="add-module-content">
              <div className="add-module-header">
                <h2 className="add-module-title">Add Module to Program</h2>
                <p className="add-module-subtitle">You are about to add the following module:</p>
              </div>

              <div className="module-preview-section">
                {selectedModuleForModal && (
                  <div className="module-preview-card">
                    <div className="module-preview-content">
                      <div className="module-preview-title-container">
                        <img src="/module.svg" alt="Module" className="module-icon" />
                        <div className="module-preview-title">{selectedModuleForModal.course_title}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="module-info-section">
                <div className="module-info-card">
                  <div className="module-info-content">
                    <div className="module-info-header">
                      <div className="module-info-icon-container">
                        <img src="/icons8-idea.svg" alt="Idea" className="idea-icon" />
                        <h3 className="module-info-title">What is Module?</h3>
                      </div>
                      <p className="module-info-description">
                        Modules allow students to focus on specific skills. Upon completion, student from this program will earn a separate certificate for this module.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="add-module-actions">
                <button className="add-module-cancel-btn" onClick={handleCloseAddModuleModal}>
                  Cancel
                </button>
                <button className="add-module-confirm-btn" onClick={handleNextToModuleDetails}>
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="delete-confirmation-overlay" onClick={cancelDeleteCourse}>
          <div className="delete-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirmation-content">
              <div className="delete-confirmation-header">
                <div className="warning-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L1 21H23L12 2Z" stroke="#CB0000" strokeWidth="2" fill="none"/>
                    <path d="M12 8V14" stroke="#CB0000" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 18H12.01" stroke="#CB0000" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <h2 className="delete-confirmation-title">Delete Course</h2>
              </div>
              
              <div className="delete-confirmation-message">
                <p className="delete-message-paragraph">
                  Deleting this course will affect your evaluation score.<br />
                  You can add the course back from your recommended courses later.
                </p>
                <p className="delete-message-question">
                  Are you sure you want to delete this course?
                </p>
              </div>
              
              <div className="delete-confirmation-actions">
                <button 
                  className="delete-cancel-btn" 
                  onClick={cancelDeleteCourse}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-delete-btn" 
                  onClick={confirmDeleteCourse}
                >
                  Yes, delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification Container for displaying notifications */}
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
    </div>
  );
};

export default CourseManagement;
